(() => {
  const SECTION_ID = 'cart-drawer-section';
  const SECTION_WRAPPER_ID = `shopify-section-${SECTION_ID}`;
  const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
  let pendingHydration = null;
  let pendingCartMutation = null;
  let cachedSectionHTML = null;
  let hydrated = false;

  const section = () => document.getElementById(SECTION_WRAPPER_ID);
  const drawer = () => document.getElementById('cart-drawer');

  function setLoadingState() {
    const shell = section()?.querySelector('[data-deferred-cart-state]');
    if (!shell) return;
    shell.dataset.deferredCartState = 'loading';
    shell.setAttribute('aria-busy', 'true');
    const message = shell.querySelector('[data-deferred-cart-message]');
    if (message) message.textContent = 'Loading your cart…';
    shell.querySelector('[data-deferred-cart-recovery]')?.setAttribute('hidden', '');
  }

  function setErrorState() {
    const shell = section()?.querySelector('[data-deferred-cart-state]');
    if (!shell) return;
    shell.dataset.deferredCartState = 'error';
    shell.setAttribute('aria-busy', 'false');
    const message = shell.querySelector('[data-deferred-cart-message]');
    if (message) message.textContent = 'We could not load your cart.';
    shell.querySelector('[data-deferred-cart-recovery]')?.removeAttribute('hidden');
    shell.querySelector('[data-deferred-cart-retry]')?.focus();
  }

  function recreateScript(inertScript) {
    const executable = document.createElement('script');
    for (const { name, value } of inertScript.attributes) executable.setAttribute(name, value);
    executable.textContent = inertScript.textContent;
    return executable;
  }

  async function applySectionResponse(sectionHTML) {
    const currentSection = section();
    const currentDrawer = drawer();
    const currentDialog = currentDrawer?.querySelector(':scope > dialog');
    if (!currentSection || !currentDrawer || !currentDialog) throw new Error('Cart drawer shell is missing');

    const responseDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const responseSection = responseDocument.getElementById(SECTION_WRAPPER_ID);
    const responseDrawer = responseSection?.querySelector('#cart-drawer');
    const responseDialog = responseDrawer?.querySelector(':scope > dialog');
    if (!responseSection || !responseDrawer || !responseDialog) {
      throw new Error('Cart drawer section response is incomplete');
    }

    const wasOpen = currentDrawer.hasAttribute('open');
    for (const attribute of [...currentDrawer.attributes]) {
      if (!['id', 'open', 'style'].includes(attribute.name)) currentDrawer.removeAttribute(attribute.name);
    }
    for (const { name, value } of responseDrawer.attributes) {
      if (!['id', 'open', 'style'].includes(name)) currentDrawer.setAttribute(name, value);
    }
    const executableScripts = [];
    responseDialog.querySelectorAll('script').forEach((script) => {
      const marker = document.createComment('deferred cart script');
      executableScripts.push([marker, recreateScript(script)]);
      script.replaceWith(marker);
    });

    for (const attribute of [...currentDialog.attributes]) {
      if (attribute.name !== 'open') currentDialog.removeAttribute(attribute.name);
    }
    for (const { name, value } of responseDialog.attributes) {
      if (name !== 'open') currentDialog.setAttribute(name, value);
    }
    currentDialog.replaceChildren(...responseDialog.childNodes);

    currentSection.querySelectorAll('[data-deferred-cart-response]').forEach((node) => node.remove());
    for (const responseNode of [...responseSection.children]) {
      if (responseNode === responseDrawer) continue;
      const clone = responseNode.tagName === 'SCRIPT' ? recreateScript(responseNode) : responseNode.cloneNode(true);
      clone.setAttribute?.('data-deferred-cart-response', '');
      currentSection.insertBefore(clone, currentDrawer);
    }

    for (const [marker, executable] of executableScripts) marker.replaceWith(executable);

    currentDrawer.removeAttribute('data-deferred-cart-drawer');
    currentDrawer.removeAttribute('no-persist');
    currentSection.dataset.deferredCartHydrated = 'true';
    hydrated = true;

    await Promise.all([
      customElements.whenDefined('cart-drawer-component'),
      customElements.whenDefined('cart-items-component'),
    ]);

    if (wasOpen) {
      currentDrawer.setAttribute('open', '');
      currentDialog.setAttribute('open', '');
      requestAnimationFrame(() => currentDialog.querySelector('.theme-drawer__close-button')?.focus());
    }
  }

  function sectionURL() {
    // Render the section in the context of the current storefront template.
    // The cart template intentionally omits the drawer, so requesting this
    // section from /cart returns an empty Shopify section wrapper.
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', SECTION_ID);
    return url;
  }

  async function fetchSection() {
    const response = await fetch(sectionURL(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Cart drawer request failed: ${response.status}`);
    return response.text();
  }

  function hydrate(sectionHTML = cachedSectionHTML) {
    if (hydrated && !sectionHTML) return Promise.resolve();
    if (pendingHydration) return pendingHydration;
    setLoadingState();
    pendingHydration = Promise.resolve(sectionHTML || fetchSection())
      .then((html) => {
        cachedSectionHTML = null;
        return applySectionResponse(html);
      })
      .catch((error) => {
        setErrorState();
        console.warn('[deferred-cart-drawer] Cart hydration failed.', error);
        throw error;
      })
      .finally(() => {
        pendingHydration = null;
      });
    return pendingHydration;
  }

  function open() {
    const cartDrawer = drawer();
    if (!cartDrawer) {
      window.location.assign(Theme.routes.cart_url || '/cart');
      return Promise.resolve();
    }

    if (typeof cartDrawer.open === 'function') cartDrawer.open();
    else customElements.whenDefined('theme-drawer').then(() => cartDrawer.open?.());
    if (pendingCartMutation) return pendingCartMutation.then(() => hydrate()).catch(() => {});
    return hydrate().catch(() => {});
  }

  function handleCartTrigger(event) {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-testid="cart-drawer-trigger"]')
      : null;
    if (!trigger || event.defaultPrevented) return;
    if (event instanceof MouseEvent && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event.preventDefault();
    open();
  }

  function handleRetry(event) {
    if (!(event.target instanceof Element) || !event.target.closest('[data-deferred-cart-retry]')) return;
    hydrate().catch(() => {});
  }

  function handleCartUpdate(event) {
    if (hydrated || !event.promise) return;
    const shouldAutoOpen = section()?.dataset.autoOpen === 'true' && event.action === 'add';
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    if (shouldAutoOpen && !sourceModal) {
      const cartDrawer = drawer();
      if (typeof cartDrawer?.open === 'function') cartDrawer.open();
      else customElements.whenDefined('theme-drawer').then(() => cartDrawer?.open?.());
    }

    pendingCartMutation = event.promise.then(({ detail }) => {
      if (detail?.didError) return;
      const response = detail?.sections?.[SECTION_ID];
      if (!shouldAutoOpen) {
        cachedSectionHTML = response || null;
        return;
      }
      const load = () => hydrate(response).then(() => {
        if (shouldAutoOpen && !drawer()?.hasAttribute('open')) drawer()?.open?.();
      });

      if (shouldAutoOpen && sourceModal?.open) sourceModal.addEventListener('close', () => load().catch(() => {}), { once: true });
      else load().catch(() => {});
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[deferred-cart-drawer] Cart update rejected.', error);
    }).finally(() => {
      pendingCartMutation = null;
    });
  }

  document.addEventListener('click', handleCartTrigger);
  document.addEventListener('click', handleRetry);
  document.addEventListener(CART_LINES_UPDATE_EVENT, handleCartUpdate);

  window.CultureHouseDeferredCart = { open, hydrate };
})();
